import { create } from 'zustand';

import type { MfaStatus } from 'models/mfa';
import type { OAuthProvider } from 'utils/oauth';

type TwoFactorAction =
    | 'login'
    | 'config'
    | 'withdrawal'
    | 'deposit-limit'
    | 'self-exclusion'
    | 'trading-limits'
    | 'daily-session-time-limit'
    | 'update-passkey';

interface TwoFactorAuthState {
    code: string;
    email: string;
    password: string;
    action: TwoFactorAction;
    rememberDevice: boolean;
    mfaRequiredToken: string;
    redirectUrl: string;
    /** Challenge method for the current flow; null until a login/config flow sets it. */
    mfaStatus: MfaStatus | null;
    /** Method being configured; null outside a config flow. */
    newMfaStatus: MfaStatus | null;
    provider: OAuthProvider | undefined;
    phone: string | undefined;
    mfaSentEmail: string | undefined;
    observedUserId: string;
    /** Whether the account-reactivation step is allowed after this sign-in attempt. */
    allowReactivation: boolean;
    /** Whether the MFA-specific account-reactivation step is allowed. */
    allowMFAReactivation: boolean;
    actions: {
        setCode: (val: string) => void;
        setEmail: (val: string) => void;
        setPhone: (val: string) => void;
        setMfaSentEmail: (val: string) => void;
        setPassword: (val: string) => void;
        setAction: (val: TwoFactorAction) => void;
        toggleRememberDevice: () => void;
        setProvider: (val: OAuthProvider) => void;
        setMfaStatus: (val: MfaStatus) => void;
        setNewMfaStatus: (val: MfaStatus) => void;
        setObservedUserId: (val: string) => void;
        setMfaRequiredToken: (val: string) => void;
        setRedirectUrl: (val: string) => void;
        setAllowReactivation: (val: boolean) => void;
        setAllowMFAReactivation: (val: boolean) => void;
        reset: () => void;
    };
}

const initialState = {
    code: '',
    email: '',
    password: '',
    action: 'login' as TwoFactorAction,
    rememberDevice: false,
    mfaRequiredToken: '',
    redirectUrl: '/',
    // Null, not a real method: an unset store must never claim a challenge type
    // (a refresh on the 2FA page used to render an authenticator prompt for
    // SMS-method accounts).
    mfaStatus: null as MfaStatus | null,
    newMfaStatus: null as MfaStatus | null,
    provider: undefined as OAuthProvider | undefined,
    phone: undefined as string | undefined,
    mfaSentEmail: undefined as string | undefined,
    observedUserId: '',
    allowReactivation: false,
    allowMFAReactivation: false,
};

export const useTwoFactorAuthStore = create<TwoFactorAuthState>()((set) => ({
    ...initialState,
    actions: {
        setCode: (val) => set({ code: val }),
        setEmail: (val) => set({ email: val }),
        setPhone: (val) => set({ phone: val }),
        setMfaSentEmail: (val) => set({ mfaSentEmail: val }),
        setPassword: (val) => set({ password: val }),
        setAction: (val) => set({ action: val }),
        toggleRememberDevice: () => set((state) => ({ rememberDevice: !state.rememberDevice })),
        setProvider: (val) => set({ provider: val }),
        setMfaStatus: (val) => set({ mfaStatus: val }),
        setNewMfaStatus: (val) => set({ newMfaStatus: val }),
        setObservedUserId: (val) => set({ observedUserId: val }),
        setMfaRequiredToken: (val) => set({ mfaRequiredToken: val }),
        setRedirectUrl: (val) => set({ redirectUrl: val }),
        setAllowReactivation: (val) => set({ allowReactivation: val }),
        setAllowMFAReactivation: (val) => set({ allowMFAReactivation: val }),
        reset: () =>
            set({
                code: '',
                email: '',
                password: '',
                action: 'login',
                rememberDevice: false,
                mfaRequiredToken: '',
                redirectUrl: '/',
                mfaStatus: null,
                newMfaStatus: null,
                provider: undefined,
                phone: undefined,
                observedUserId: '',
                allowReactivation: false,
                allowMFAReactivation: false,
            }),
    },
}));

export const selectIsConfig = (state: TwoFactorAuthState): boolean => state.action === 'config';
